/**
 * Module-level cache of `/related` payloads, keyed `${apiName}/${recordId}`.
 *
 * It is module-level on purpose: it survives RecordDetail unmounts, so
 * navigating away from a record and back does not re-fetch its related lists.
 * That is also its hazard — the entry outlives the component that wrote it,
 * so any mutation made elsewhere leaves a stale snapshot behind for up to
 * TTL_MS. A browser reload clears it (fresh module), which is why the symptom
 * only ever showed up on in-app navigation and vanished on Ctrl+F5.
 *
 * Extracted from App.js 2026-08-30 so the modals that mutate related records
 * can invalidate it. Behaviour is unchanged — same Map, same TTL, same
 * delete-time patch.
 *
 * INVARIANT: anything that writes an OpportunityLineItem (or any related
 * record) must either patch this cache or drop the parent's entry. The
 * backend is not the problem — its own invalidation chain
 * (`_invalidate_related_cache_for_new_record` + the recent_writes overlay)
 * was measured returning correct data at t=0, t=70s and t=140s across
 * workers, with and without a cache-bust parameter.
 */

export const relatedRecordsCache = new Map();
export const RELATED_CACHE_TTL_MS = 60_000;

/**
 * Drop the parent's whole entry so the next read goes to the network.
 *
 * Used after a mutation whose effect on the related lists is not known
 * row-by-row — adding several products at once, or a reconciliation that may
 * have inserted, grown, shrunk or deleted rows. Patching would mean
 * replicating the server's view; dropping is honest about not knowing.
 *
 * Deliberately NOT touching TTL and NOT adding a cache-bust parameter to the
 * fetch: the entry is simply gone, so the existing code path fetches.
 */
export function invalidateRelatedCacheForParent(parentApiName, parentRecordId) {
  if (!parentApiName || !parentRecordId) return false;
  return relatedRecordsCache.delete(`${parentApiName}/${parentRecordId}`);
}

/**
 * Patch a single deleted row out of the parent's cached entry.
 *
 * 2026-06-17 owner-spec: the optimistic UI patch (setRelatedRecords filter)
 * did not touch this cache, so F5 or navigate-away-and-back brought the
 * deleted row back — reported as "אם מרעננים היא חוזרת". Kept as a patch
 * rather than a drop because the effect IS known row-by-row here, and
 * keeping the rest of the payload avoids a refetch.
 *
 * Also drops the deleted record's OWN entry: its related lists no longer
 * matter.
 */
export function invalidateRelatedCacheOnDelete(parentApiName, parentRecordId,
                                               deletedObjectName, deletedRecordId) {
  if (!parentApiName || !parentRecordId) return;
  const parentKey = `${parentApiName}/${parentRecordId}`;
  const cached = relatedRecordsCache.get(parentKey);
  if (cached) {
    const prevList = cached.data?.[deletedObjectName] || [];
    const nextList = prevList.filter(r => r?.Id !== deletedRecordId);
    const prevCount = (cached.counts?.[deletedObjectName] ?? prevList.length);
    relatedRecordsCache.set(parentKey, {
      ...cached,
      data: { ...cached.data, [deletedObjectName]: nextList },
      counts: { ...cached.counts, [deletedObjectName]: Math.max(0, prevCount - 1) },
      timestamp: Date.now(),
    });
  }
  if (deletedObjectName && deletedRecordId) {
    relatedRecordsCache.delete(`${deletedObjectName}/${deletedRecordId}`);
  }
}
